// Type definitions
export interface ProfileAliasDto {
  idAliasID: string;
  chrBaseUserName: string;
  chrAliasName: string;
  chrDisplayName: string;
  intAliasType: number;  // 1=Public, 2=Private, 3=Semi-Anonymous, 4=Business/Credentialed
  aliasTypeName: string; // Human-readable: "Public", "Private", "Semi-Anonymous", "Business"
  chrCredentialType?: string; // For type 4: "VENDOR", "MARKETER", "DEVELOPER"
  bitIsActive: boolean;
  bitIsPrimary: boolean;
  bitTrueNameLocked?: boolean;
  bitIdVerified?: boolean;
  dtmCreateDate: string;
  dtmLastUsedDate?: string;
}

export interface CreateAliasRequestDto {
  chrAliasName: string;
  chrDisplayName: string;
  intAliasType: number; // 1=Public, 2=Private, 3=Semi-Anonymous
}

export interface UpdateAliasRequestDto {
  chrDisplayName?: string;
  intAliasType?: number; // 1=Public, 2=Private, 3=Semi-Anonymous
  bitIsActive?: boolean;
}

export interface TrueNameLockRequestDto {
  lockEnabled: boolean;
}

export interface ProfileDto {
  idProfileID: string;
  idAliasID: string;
  chrAliasName: string;
  chrDisplayName: string;
  txtBio?: string;
  chrProfileImageURL?: string;
  chrCoverImageURL?: string;
  chrLocation?: string;
  chrWebsiteURL?: string;
  intVisibilityLevel: number;
  chrJobTitle?: string;
  chrCompany?: string;
  txtSkills?: string;
  chrEducation?: string;
  txtProfessionalBio?: string;
  dtmCreateDate?: string;
  dtmLastModifiedDate?: string;
  socialLinks: SocialLinkDto[];
  interests: InterestDto[];
  // Business account info (only populated for business/credentialed aliases)
  intAliasType?: number;
  createdByUsername?: string;
  businessName?: string;
  credentialType?: string;
}

export interface ProfileAccessDto {
  profile: ProfileDto;
  canEdit: boolean;
  isTeamMemberProfile: boolean;
  isPremiumAliasProfile: boolean;
}

export interface UpdateProfileRequestDto {
  chrDisplayName?: string;
  txtBio?: string;
  chrProfileImageURL?: string;
  chrCoverImageURL?: string;
  chrLocation?: string;
  chrWebsiteURL?: string;
  intVisibilityLevel?: number;
  chrJobTitle?: string;
  chrCompany?: string;
  txtSkills?: string;
  chrEducation?: string;
  txtProfessionalBio?: string;
}

export interface SocialLinkDto {
  idSocialLinkID: string;
  idProfileID: string;
  chrPlatform: string;
  chrPlatformURL: string;
  chrPlatformUsername?: string;
  intDisplayOrder: number;
  bitIsActive: boolean;
}

export interface CreateSocialLinkDto {
  chrPlatform: string;
  chrPlatformURL: string;
  chrPlatformUsername?: string;
  intDisplayOrder?: number;
}

export interface InterestDto {
  idInterestID: string;
  idProfileID: string;
  chrInterestTag: string;
  chrInterestCategory?: string;
}

export interface CreateInterestDto {
  chrInterestTag: string;
  chrInterestCategory?: string;
}

class SocialService {
  private readonly baseUrl: string;

  constructor() {
    // Profile/Social endpoints are now part of MailBox.API
    // Use the same env var as the main API service for consistency
    // - Development: Uses localhost:5220 (MailBox.API)
    // - Production: Uses empty baseURL (IIS reverse proxy routes /api to API server)
    const envUrl = import.meta.env.VITE_API_BASE_URL;
    let apiBaseUrl: string;
    
    if (envUrl && envUrl.trim() !== '') {
      apiBaseUrl = envUrl;
    } else if (import.meta.env.PROD) {
      apiBaseUrl = '';  // Relative URL - browser uses current origin
    } else {
      apiBaseUrl = 'http://localhost:5220';
    }
    
    this.baseUrl = `${apiBaseUrl}/api`;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Alias Management
  async getMyAliases(): Promise<ProfileAliasDto[]> {
    const response = await fetch(`${this.baseUrl}/alias/me`, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch aliases');
    }
    return response.json();
  }

  async getAliasByName(aliasName: string): Promise<ProfileAliasDto> {
    const response = await fetch(`${this.baseUrl}/alias/name/${aliasName}`);
    if (!response.ok) {
      throw new Error('Failed to fetch alias');
    }
    return response.json();
  }

  async createAlias(request: CreateAliasRequestDto): Promise<ProfileAliasDto> {
    const response = await fetch(`${this.baseUrl}/alias`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create alias');
    }
    return response.json();
  }

  async updateAlias(aliasId: string, request: UpdateAliasRequestDto): Promise<ProfileAliasDto> {
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    const text = await response.text();
    if (!response.ok) {
      let message = 'Failed to update alias';
      if (text) {
        try {
          const error = JSON.parse(text);
          message = error.message || message;
        } catch {
          message = text.slice(0, 100) || message;
        }
      }
      throw new Error(message);
    }
    if (!text) {
      throw new Error('Server returned no data');
    }
    try {
      return JSON.parse(text) as ProfileAliasDto;
    } catch {
      throw new Error('Invalid response from server');
    }
  }

  async setPrimaryAlias(aliasId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}/primary`, {
      method: 'PUT',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to set primary alias');
    }
  }

  async deleteAlias(aliasId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete alias');
    }
  }

  async setTrueNameLock(aliasId: string, lockEnabled: boolean): Promise<ProfileAliasDto> {
    const request: TrueNameLockRequestDto = { lockEnabled };
    const response = await fetch(`${this.baseUrl}/alias/${aliasId}/true-name-lock`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    const text = await response.text();
    if (!response.ok) {
      let message = 'Failed to update true name lock';
      if (text) {
        try {
          const error = JSON.parse(text);
          message = error.message || message;
        } catch {
          message = text.slice(0, 120) || message;
        }
      }
      throw new Error(message);
    }

    return text ? (JSON.parse(text) as ProfileAliasDto) : ({} as ProfileAliasDto);
  }

  // Profile Management
  async getProfile(aliasName: string): Promise<ProfileAccessDto> {
    const response = await fetch(`${this.baseUrl}/profile/alias/${aliasName}`, {
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }
    return response.json();
  }

  async getProfileByAliasId(aliasId: string): Promise<ProfileDto | null> {
    const response = await fetch(`${this.baseUrl}/profile/${aliasId}`, {
      headers: this.getAuthHeaders()
    });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }
    return response.json();
  }

  async updateProfile(aliasId: string, request: UpdateProfileRequestDto): Promise<ProfileDto> {
    const response = await fetch(`${this.baseUrl}/profile/${aliasId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      throw new Error('Failed to update profile');
    }
    return response.json();
  }

  async searchProfiles(interest: string): Promise<ProfileDto[]> {
    const response = await fetch(`${this.baseUrl}/profile/search?interest=${encodeURIComponent(interest)}`);
    if (!response.ok) {
      throw new Error('Failed to search profiles');
    }
    return response.json();
  }

  // Social Links Management
  async getSocialLinks(profileId: string): Promise<SocialLinkDto[]> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks`);
    if (!response.ok) {
      throw new Error('Failed to fetch social links');
    }
    return response.json();
  }

  async addSocialLink(profileId: string, link: CreateSocialLinkDto): Promise<SocialLinkDto> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(link)
    });
    if (!response.ok) {
      throw new Error('Failed to add social link');
    }
    return response.json();
  }

  async updateSocialLink(profileId: string, linkId: string, link: CreateSocialLinkDto): Promise<SocialLinkDto> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks/${linkId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(link)
    });
    if (!response.ok) {
      throw new Error('Failed to update social link');
    }
    return response.json();
  }

  async deleteSocialLink(profileId: string, linkId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/sociallinks/${linkId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to delete social link');
    }
  }

  // Interests Management
  async getInterests(profileId: string): Promise<InterestDto[]> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/interests`);
    if (!response.ok) {
      throw new Error('Failed to fetch interests');
    }
    return response.json();
  }

  async addInterest(profileId: string, interest: CreateInterestDto): Promise<InterestDto> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/interests`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(interest)
    });
    if (!response.ok) {
      throw new Error('Failed to add interest');
    }
    return response.json();
  }

  async deleteInterest(profileId: string, interestId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/profile/${profileId}/interests/${interestId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to delete interest');
    }
  }
}

export default new SocialService();

